import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import ControlledRadioButtonGroup from 'src/shared/components/form/controlledRadioButtonGroup';
import { ControlledCheckboxNew } from 'src/shared/components/form/controlledCheckboxNew';
import * as yup from 'yup';
import { Schema } from 'yup';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  Button,
  Grid,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Divider,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { CheckCircle, Error as ErrorIcon, Send } from '@mui/icons-material';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { HeaderSubTitle } from 'src/modules/dashboard/components/atoms/headerSubTitle';
import { createCustomer, sendInvitation, InstallCustomerResult } from 'src/shared/services/installCustomerService';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { toast } from 'react-toastify';
import ControlledTextEditor from 'src/shared/components/form/controlledTextEditor';
import { InstallCustomerRequest } from '../models/installCustomerRequest';

const InstallCustomerForm = () => {
  const { t } = useTranslation(['common', 'installCustomer']);
  const isMounted = useIsMounted();
  const requiredMessage = t('common:validationMessages.required');

  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [installResult, setInstallResult] = useState<InstallCustomerResult | null>(null);
  const [mailSent, setMailSent] = useState(false);

  const InstallCustomerRequestPostSchema: Schema<InstallCustomerRequest> = yup.object().shape({
    countryAssociationStructure: yup.number().required(requiredMessage),
    trustAccount: yup.number().default(0),
    centralConfig: yup.number().required(requiredMessage),
    ocrSetting: yup.number().required(requiredMessage),
    countryName: yup.string().required(requiredMessage),
    dbLoginName: yup.string().required(requiredMessage),
    dbUserName: yup.string().required(requiredMessage),
    userEmail: yup.string().email().required(requiredMessage),
    userFirstName: yup.string().required(requiredMessage),
    userLastName: yup.string().required(requiredMessage),
    userName: yup.string().required(requiredMessage),
    userPassword: yup.string().required(requiredMessage),
    privateName: yup.string().required(requiredMessage),
    privateCO: yup.string().required(requiredMessage),
    privateAddress: yup.string().required(requiredMessage),
    privateZip: yup.string().required(requiredMessage),
    privateCity: yup.string().required(requiredMessage),
    privateCountry: yup.string().required(requiredMessage),
    privatePhone: yup.string().required(requiredMessage),
    privateEmail: yup.string().email().required(requiredMessage),
    privateEmailPaymentsNotifications: yup.string().email().required(requiredMessage),
    privateEmailSendingsNotifications: yup.string().email().required(requiredMessage),
    publicCO: yup.string().required(requiredMessage),
    publicAddress: yup.string().required(requiredMessage),
    publicZip: yup.string().required(requiredMessage),
    publicCity: yup.string().required(requiredMessage),
    publicCountry: yup.string().required(requiredMessage),
    publicPhone: yup.string().required(requiredMessage),
    publicEmail: yup.string().email().required(requiredMessage),
    publicOrgNo: yup.string().required(requiredMessage),
    publicPaymentsAccount: yup.string().required(requiredMessage),
    publicWebsite: yup.string().required(requiredMessage),
    publicExpireDate: yup.string().required(requiredMessage),
    invAccountNo: yup.string().required(requiredMessage),
  });

  const generatePassword = () =>
    Array.from(crypto.getRandomValues(new Uint8Array(16)), (b) => 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789'[b % 55])
      .join('')
      .substring(0, 25);

  const formMethods = useForm({
    resolver: yupResolver(InstallCustomerRequestPostSchema),
    defaultValues: {
      dbServerName: 'fsweb2',
      memberNumberType: '',
      createDataMreg: false,
      defaultDb: 'fs-medlem',
      style: 'fs',
      districtLabel: 'Län',
      localLabel: 'Lokalförening',
      systemName: 'Föreningssupport.se',
      countryAssociationStructure: 0,
      trustAccount: 0,
      centralConfig: 0,
      ocrSetting: 0,
      countryName: '',
      dbLoginName: '',
      dbUserName: '',
      userEmail: '',
      userFirstName: '',
      userLastName: '',
      userName: '',
      userPassword: generatePassword(),
      privateName: '',
      privateCO: '',
      privateAddress: '',
      privateZip: '',
      privateCity: '',
      privateCountry: 'Sverige',
      privatePhone: '',
      privateEmail: '',
      privateEmailPaymentsNotifications: '',
      privateEmailSendingsNotifications: '',
      privatePaymentsAccount: '',
      publicCO: '',
      publicAddress: '',
      publicZip: '',
      publicCity: '',
      publicCountry: 'Sverige',
      publicPhone: '',
      publicEmail: '',
      publicEmailResposible: '',
      publicOrgNo: '',
      publicPaymentsAccount: '0',
      publicWebsite: '',
      publicExpireDate: '1231',
      publicGracePeriod: '',
      invAccountNo: '0',
      invBIC: '',
      invIBAN: '',
      moduleMemberReg: true,
      moduleInvoicing: true,
      moduleGifts: false,
      moduleEvents: false,
      serviceAutogiro: false,
      servicePaymentPlus: false,
      serviceSpar: false,
      serviceSupportPlus: false,
      createDemoGifts: false,
      invitationSubject: 'Välkommen till Föreningssupport',
      invitationBody: '',
    },
  });
  const { handleSubmit, watch, setValue, getValues, trigger } = formMethods;
  const orgStructure = watch('countryAssociationStructure');
  const userEmail = watch('userEmail');
  const userFirstName = watch('userFirstName');
  const userLastName = watch('userLastName');
  const countryName = watch('countryName');
  const privatePhone = watch('privatePhone');
  const isSingleAssociation = orgStructure === 0;

  // Nollställ createDataMreg vid enskild förening
  useEffect(() => {
    if (isSingleAssociation) {
      setValue('createDataMreg', false);
    }
  }, [isSingleAssociation, setValue]);

  // Generera kortnamn från organisationsnamn
  useEffect(() => {
    if (countryName) {
      const shortName = countryName
        .toLowerCase()
        .replace(/å/g, 'a')
        .replace(/ä/g, 'a')
        .replace(/ö/g, 'o')
        .replace(/é/g, 'e')
        .replace(/[^a-z0-9]/g, '')
        .substring(0, 20);
      setValue('dbLoginName', shortName, { shouldDirty: true });
      setValue('dbUserName', shortName, { shouldDirty: true });
    }
  }, [countryName, setValue]);

  // Enskild förening → OCR ja, centralConfig nej
  useEffect(() => {
    if (isSingleAssociation) {
      setValue('ocrSetting', 1);
      setValue('centralConfig', 0);
    }
  }, [isSingleAssociation, setValue]);

  // Kopiera telefon
  useEffect(() => {
    if (privatePhone) setValue('publicPhone', privatePhone, { shouldDirty: true });
  }, [privatePhone, setValue]);

  // Kopiera e-post
  useEffect(() => {
    if (userEmail) {
      setValue('userName', userEmail);
      setValue('privateEmail', userEmail);
      setValue('privateEmailSendingsNotifications', userEmail);
      setValue('publicEmail', userEmail);
      setValue('publicEmailResposible', userEmail);
    }
  }, [userEmail, setValue]);

  // Kopiera namn
  useEffect(() => {
    const fullName = `${userFirstName || ''} ${userLastName || ''}`.trim();
    if (fullName) {
      setValue('privateName', fullName);
      setValue('privateCO', fullName);
      setValue('publicCO', fullName);
    }
  }, [userFirstName, userLastName, setValue]);

  // Uppdatera inbjudningstext (HTML)
  const dbLoginName = watch('dbLoginName');
  const userName = watch('userName');
  useEffect(() => {
    const dbLogin = dbLoginName || '[kortnamn]';
    const uName = userName || '[användarnamn]';
    const body = `<p>Hej!</p>
<p>Ert konto är nu skapat.</p>
<p><strong>Inloggningssida:</strong><br/>
<a href="https://app.foreningssupport.se/login?tenant=${dbLogin}&type=user">https://app.foreningssupport.se/login?tenant=${dbLogin}&type=user</a></p>
<p><strong>Användarnamn:</strong><br/>
${uName}</p>
<p><strong>Lösenord:</strong><br/>
Hämtas ut via länken Skapa lösenord på inloggningssidan</p>
<p><strong>Support</strong><br/>
Om du behöver hjälp är du välkommen att kontakta vår support, antingen via <a href="https://foreningssupport.se/kontakt/">foreningssupport.se/kontakt</a> eller <a href="mailto:support@foreningssupport.se">support@foreningssupport.se</a>.</p>
<p>På vår webbplats <a href="https://www.foreningssupport.se">www.foreningssupport.se</a> kan du läsa mer om oss, våra produkter och tjänster.</p>`;
    setValue('invitationBody', body);
  }, [dbLoginName, userName, setValue]);

  const onSubmit = async (data: InstallCustomerRequest) => {
    setLoading(true);
    try {
      const res = await createCustomer(data);
      if (isMounted()) {
        setInstallResult(res.data);
        setActiveStep(4);
      }
    } catch (error) {
      if (isMounted()) {
        ConsoleHelper.log(error);
        toast.error('Ett fel uppstod vid uppsättningen' as string);
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  };

  const stepLabels = ['Kunduppgifter', 'Moduler', 'Inbjudan', 'Sammanfattning', 'Resultat'];

  // Fält som ska valideras per steg
  const step1Fields = [
    'countryName',
    'dbLoginName',
    'dbUserName',
    'userEmail',
    'userFirstName',
    'userLastName',
    'userName',
    'userPassword',
    'privateName',
    'privateCO',
    'privateAddress',
    'privateZip',
    'privateCity',
    'privateCountry',
    'privatePhone',
    'privateEmail',
    'privateEmailPaymentsNotifications',
    'privateEmailSendingsNotifications',
    'publicCO',
    'publicAddress',
    'publicZip',
    'publicCity',
    'publicCountry',
    'publicPhone',
    'publicEmail',
    'publicOrgNo',
    'publicPaymentsAccount',
    'publicWebsite',
    'publicExpireDate',
    'invAccountNo',
  ] as const;

  const handleNext = async () => {
    if (activeStep === 0) {
      const valid = await trigger([...step1Fields]);
      if (!valid) return;
    }
    setActiveStep((prev) => prev + 1);
  };
  const handleBack = () => setActiveStep((prev) => prev - 1);

  return (
    <>
      <FormProvider {...formMethods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress role='alert' aria-busy='true' aria-label={t('common:statusMessages.loading')} />
            </Box>
          )}
          {!loading && (
            <>
              <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
                {stepLabels.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              <Divider sx={{ mb: 3 }} />

              {/* Flik 1: Kunduppgifter */}
              {activeStep === 0 && (
                <GridWrapper>
                  <Grid item xs={12}>
                    <HeaderSubTitle label={t('common:sideMenu.organization')} />
                  </Grid>
                  <Grid item xs={12}>
                    <ControlledRadioButtonGroup
                      name='countryAssociationStructure'
                      defaultValue={0}
                      required
                      label='Organisationsstruktur'
                      items={[
                        { value: 0, label: 'Enskild Förening' },
                        { value: 1, label: 'Förbund + Föreningar' },
                        { value: 2, label: 'Förbund, Distrikt och Föreningar' },
                      ]}
                    />
                  </Grid>
                  {!isSingleAssociation && (
                    <>
                      <Grid item xs={6} sm={3}>
                        <ControlledRadioButtonGroup
                          name='ocrSetting'
                          defaultValue={0}
                          required
                          label='Central inkassering'
                          items={[
                            { value: 0, label: 'Nej' },
                            { value: 1, label: 'Ja' },
                          ]}
                        />
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <ControlledRadioButtonGroup
                          name='centralConfig'
                          defaultValue={0}
                          required
                          label='Förbundsgemensam avgift'
                          items={[
                            { value: 0, label: 'Nej' },
                            { value: 1, label: 'Ja' },
                          ]}
                        />
                      </Grid>
                    </>
                  )}
                  <Grid item xs={12}>
                    <ControlledTextField
                      name='countryName'
                      placeholder='Förbundets/Organisationens namn'
                      label={t('installCustomer:formFields.countryName')}
                      inputProps={{ maxLength: 50 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField
                      name='dbLoginName'
                      placeholder='Genereras automatiskt'
                      label={t('installCustomer:formFields.dbLoginName')}
                      inputProps={{ maxLength: 50 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField
                      name='dbUserName'
                      placeholder='Genereras automatiskt'
                      label={t('installCustomer:formFields.dbUserName')}
                      inputProps={{ maxLength: 50 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <HeaderSubTitle label='Användare' />
                  </Grid>
                  <Grid item xs={12}>
                    <ControlledTextField
                      name='userEmail'
                      label={t('installCustomer:formFields.userEmail')}
                      inputProps={{ maxLength: 50 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField
                      name='userFirstName'
                      label={t('installCustomer:formFields.userFirstName')}
                      inputProps={{ maxLength: 50 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField
                      name='userLastName'
                      label={t('installCustomer:formFields.userLastName')}
                      inputProps={{ maxLength: 50 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField
                      name='userName'
                      placeholder='Autofylls från e-post'
                      label={t('installCustomer:formFields.userName')}
                      inputProps={{ maxLength: 50 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField
                      name='userPassword'
                      label={t('installCustomer:formFields.userPassword')}
                      inputProps={{ maxLength: 25 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <HeaderSubTitle label='Kund för fakturering' />
                  </Grid>
                  <Grid item xs={12}>
                    <ControlledTextField
                      name='privateName'
                      placeholder='Autofylls från användarnamn'
                      label={t('installCustomer:formFields.privateName')}
                      inputProps={{ maxLength: 50 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField
                      name='privateCO'
                      placeholder='Autofylls från användarnamn'
                      label={t('installCustomer:formFields.privateCO')}
                      inputProps={{ maxLength: 50 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField
                      name='privateAddress'
                      label={t('installCustomer:formFields.privateAddress')}
                      inputProps={{ maxLength: 50 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <ControlledTextField
                      name='privateZip'
                      label={t('installCustomer:formFields.privateZip')}
                      inputProps={{ maxLength: 50 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <ControlledTextField
                      name='privateCity'
                      label={t('installCustomer:formFields.privateCity')}
                      inputProps={{ maxLength: 50 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <ControlledTextField
                      name='privateCountry'
                      label={t('installCustomer:formFields.privateCountry')}
                      inputProps={{ maxLength: 50 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField
                      name='privatePhone'
                      label={t('installCustomer:formFields.privatePhone')}
                      inputProps={{ maxLength: 50 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField
                      name='privateEmail'
                      label={t('installCustomer:formFields.privateEmail')}
                      inputProps={{ maxLength: 50 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField
                      name='privateEmailPaymentsNotifications'
                      label={t('installCustomer:formFields.privateEmailPaymentsNotifications')}
                      inputProps={{ maxLength: 50 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField
                      name='privateEmailSendingsNotifications'
                      label={t('installCustomer:formFields.privateEmailSendingsNotifications')}
                      inputProps={{ maxLength: 50 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <HeaderSubTitle label='Organisationens publika uppgifter (syns på ex: faktura)' />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField
                      name='publicCO'
                      label={t('installCustomer:formFields.publicCO')}
                      inputProps={{ maxLength: 50 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField
                      name='publicAddress'
                      label={t('installCustomer:formFields.publicAddress')}
                      inputProps={{ maxLength: 50 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <ControlledTextField
                      name='publicZip'
                      label={t('installCustomer:formFields.publicZip')}
                      inputProps={{ maxLength: 50 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <ControlledTextField
                      name='publicCity'
                      label={t('installCustomer:formFields.publicCity')}
                      inputProps={{ maxLength: 50 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <ControlledTextField
                      name='publicCountry'
                      label={t('installCustomer:formFields.publicCountry')}
                      inputProps={{ maxLength: 50 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField
                      name='publicPhone'
                      placeholder='000-0000000'
                      label={t('installCustomer:formFields.publicPhone')}
                      inputProps={{ maxLength: 50 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField
                      name='publicEmail'
                      label={t('installCustomer:formFields.publicEmail')}
                      inputProps={{ maxLength: 50 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ControlledTextField
                      name='publicEmailResposible'
                      placeholder='Sätts bara om organisationsstruktur är Enskild förening'
                      label={t('installCustomer:formFields.publicEmailResposible')}
                      inputProps={{ maxLength: 50 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={4} lg={2}>
                    <ControlledTextField
                      name='publicOrgNo'
                      label={t('installCustomer:formFields.publicOrgNo')}
                      inputProps={{ maxLength: 50 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={8} lg={10}>
                    <ControlledTextField
                      name='publicPaymentsAccount'
                      placeholder='Skrivs ut på faktura'
                      label={t('installCustomer:formFields.publicPaymentsAccount')}
                      inputProps={{ maxLength: 50 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ControlledTextField
                      name='publicWebsite'
                      label={t('installCustomer:formFields.publicWebsite')}
                      inputProps={{ maxLength: 50 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField
                      name='publicExpireDate'
                      label={t('installCustomer:formFields.publicExpireDate')}
                      inputProps={{ maxLength: 50 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField
                      name='publicGracePeriod'
                      label={t('installCustomer:formFields.publicGracePeriod')}
                      inputProps={{ maxLength: 50 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <ControlledTextField
                      name='invAccountNo'
                      placeholder='Sätts på fakturagrupp'
                      label={t('installCustomer:formFields.invAccountNo')}
                      inputProps={{ maxLength: 50 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <ControlledTextField
                      name='invBIC'
                      label={t('installCustomer:formFields.invBIC')}
                      inputProps={{ maxLength: 50 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={3}>
                    <ControlledTextField
                      name='invIBAN'
                      label={t('installCustomer:formFields.invIBAN')}
                      inputProps={{ maxLength: 50 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button size='large' variant='contained' fullWidth disableElevation onClick={handleNext}>
                      Nästa
                    </Button>
                  </Grid>
                </GridWrapper>
              )}

              {/* Flik 2: Moduler */}
              {activeStep === 1 && (
                <GridWrapper>
                  <Grid item xs={12}>
                    <HeaderSubTitle label='Moduler' />
                  </Grid>
                  <Grid item xs={12}>
                    <ControlledCheckboxNew name='moduleMemberReg' label='Medlemsregister (ingår alltid)' defaultValue disabled />
                  </Grid>
                  <Grid item xs={12}>
                    <ControlledCheckboxNew name='moduleInvoicing' label='Fakturering (ingår alltid)' defaultValue disabled />
                  </Grid>
                  <Grid item xs={12}>
                    <ControlledCheckboxNew name='moduleGifts' label='Gåvor' />
                  </Grid>
                  <Grid item xs={12}>
                    <ControlledCheckboxNew name='moduleEvents' label='Evenemang' />
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <HeaderSubTitle label='Tilläggstjänster' />
                  </Grid>
                  <Grid item xs={12}>
                    <ControlledCheckboxNew name='serviceAutogiro' label='Autogiro' />
                  </Grid>
                  <Grid item xs={12}>
                    <ControlledCheckboxNew name='servicePaymentPlus' label='Betalning Plus' />
                  </Grid>
                  <Grid item xs={12}>
                    <ControlledCheckboxNew name='serviceSpar' label='Aktivera SPAR-uppdateringar (avtal krävs)' />
                  </Grid>
                  <Grid item xs={12}>
                    <ControlledCheckboxNew name='serviceSupportPlus' label='Support Plus' />
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                    <HeaderSubTitle label='Övrigt' />
                  </Grid>
                  {!isSingleAssociation && (
                    <Grid item xs={12}>
                      <ControlledCheckboxNew name='createDataMreg' label='Skapa exempeldata (distrikt, förening och styrelseposter)' />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <ControlledCheckboxNew name='createDemoGifts' label='Skapa exempeldata för gåvor (testgåva)' />
                  </Grid>
                  <Grid item xs={12} sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <Button variant='outlined' onClick={handleBack}>
                      Tillbaka
                    </Button>
                    <Button variant='contained' disableElevation onClick={handleNext}>
                      Nästa
                    </Button>
                  </Grid>
                </GridWrapper>
              )}

              {/* Flik 3: Inbjudan */}
              {activeStep === 2 && (
                <GridWrapper>
                  <Grid item xs={12}>
                    <HeaderSubTitle label='Inbjudan via e-post' />
                  </Grid>
                  <Grid item xs={12}>
                    <ControlledTextField name='invitationSubject' label='Ämne' InputLabelProps={{ shrink: true }} />
                  </Grid>
                  <Grid item xs={12}>
                    <ControlledTextEditor name='invitationBody' hideMenuBar hidedataPropFields rows={20} />
                  </Grid>
                  <Grid item xs={12} sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <Button variant='outlined' onClick={handleBack}>
                      Tillbaka
                    </Button>
                    <Button variant='contained' disableElevation onClick={handleNext}>
                      Nästa
                    </Button>
                  </Grid>
                </GridWrapper>
              )}

              {/* Flik 4: Sammanfattning */}
              {activeStep === 3 && (
                <GridWrapper>
                  <Grid item xs={12}>
                    <HeaderSubTitle label='Sammanfattning' />
                  </Grid>
                  <Grid item xs={12}>
                    <Alert severity='info' sx={{ mb: 2 }}>
                      Kontrollera uppgifterna nedan. När du klickar på &quot;Slutför uppsättning&quot; skapas kontot. Inbjudningsmailet skickas i
                      nästa steg.
                    </Alert>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant='subtitle2' gutterBottom>
                      Organisation
                    </Typography>
                    <Typography variant='body2'>{getValues('countryName')}</Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Kortnamn: {getValues('dbLoginName')}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Struktur:{' '}
                      {['Enskild Förening', 'Förbund + Föreningar', 'Förbund, Distrikt och Föreningar'][getValues('countryAssociationStructure')]}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant='subtitle2' gutterBottom>
                      Användare
                    </Typography>
                    <Typography variant='body2'>
                      {getValues('userFirstName')} {getValues('userLastName')}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      {getValues('userEmail')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant='subtitle2' gutterBottom>
                      Moduler
                    </Typography>
                    {getValues('moduleMemberReg') && <Typography variant='body2'>Medlemsregister</Typography>}
                    {getValues('moduleInvoicing') && <Typography variant='body2'>Fakturering</Typography>}
                    {getValues('moduleGifts') && <Typography variant='body2'>Gåvor</Typography>}
                    {getValues('moduleEvents') && <Typography variant='body2'>Evenemang</Typography>}
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant='subtitle2' gutterBottom>
                      Tilläggstjänster
                    </Typography>
                    {getValues('serviceAutogiro') && <Typography variant='body2'>Autogiro</Typography>}
                    {getValues('servicePaymentPlus') && <Typography variant='body2'>Betalning Plus</Typography>}
                    {getValues('serviceSpar') && <Typography variant='body2'>SPAR-uppdateringar</Typography>}
                    {getValues('serviceSupportPlus') && <Typography variant='body2'>Support Plus</Typography>}
                    {!getValues('serviceAutogiro') &&
                      !getValues('servicePaymentPlus') &&
                      !getValues('serviceSpar') &&
                      !getValues('serviceSupportPlus') && (
                        <Typography variant='body2' color='text.secondary'>
                          Inga
                        </Typography>
                      )}
                  </Grid>
                  <Grid item xs={12}>
                    <Divider sx={{ my: 1 }} />
                  </Grid>
                  <Grid item xs={12}>
                    <Typography variant='subtitle2' gutterBottom>
                      Inbjudan
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Ämne: {getValues('invitationSubject')}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      Mottagare: {getValues('userEmail')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sx={{ display: 'flex', gap: 1, mt: 2 }}>
                    <Button variant='outlined' onClick={handleBack}>
                      Tillbaka
                    </Button>
                    <Button size='large' variant='contained' color='success' type='submit' disableElevation disabled={loading}>
                      Slutför uppsättning
                    </Button>
                  </Grid>
                </GridWrapper>
              )}

              {/* Steg 5: Resultat */}
              {activeStep === 4 && installResult && (
                <GridWrapper>
                  <Grid item xs={12}>
                    <HeaderSubTitle label='Resultat' />
                  </Grid>
                  <Grid item xs={12}>
                    {installResult.success ? (
                      <Alert severity='success' sx={{ mb: 2 }}>
                        Uppsättningen lyckades! Alla steg genomfördes utan fel.
                      </Alert>
                    ) : (
                      <Alert severity='error' sx={{ mb: 2 }}>
                        Uppsättningen slutfördes med fel. Kontrollera stegen nedan.
                      </Alert>
                    )}
                  </Grid>
                  <Grid item xs={12}>
                    <List dense>
                      {installResult.steps.map((step) => (
                        <ListItem key={step.name}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            {step.success ? <CheckCircle color='success' /> : <ErrorIcon color='error' />}
                          </ListItemIcon>
                          <ListItemText primary={step.name} secondary={step.message} />
                        </ListItem>
                      ))}
                    </List>
                  </Grid>
                  {installResult.success && (
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant='subtitle2' gutterBottom>
                        Skicka inbjudningsmail
                      </Typography>
                      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                        Mottagare: {getValues('userEmail')}
                      </Typography>
                      {mailSent ? (
                        <Alert severity='success'>Inbjudningsmailet har skickats!</Alert>
                      ) : (
                        <Button
                          variant='contained'
                          disableElevation
                          startIcon={<Send />}
                          disabled={loading}
                          onClick={async () => {
                            setLoading(true);
                            try {
                              await sendInvitation(getValues() as InstallCustomerRequest);
                              if (isMounted()) {
                                setMailSent(true);
                                toast.success('Inbjudningsmailet har skickats!' as string);
                              }
                            } catch (error) {
                              if (isMounted()) {
                                ConsoleHelper.log(error);
                                toast.error('Kunde inte skicka mailet' as string);
                              }
                            } finally {
                              if (isMounted()) setLoading(false);
                            }
                          }}
                        >
                          Skicka inbjudningsmail
                        </Button>
                      )}
                    </Grid>
                  )}
                </GridWrapper>
              )}
            </>
          )}
        </form>
      </FormProvider>
    </>
  );
};

export default InstallCustomerForm;
